"use client";
import { currencies, DEFAULT_CURRENCY } from '@/constants';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const COOKIE_NAME = 'currency';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

interface CurrencyContextType {
  selectedCurrency: string;
  updateCurrency: (currency: string) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>(DEFAULT_CURRENCY);

  // The server no longer reads the `currency` cookie (that would force dynamic
  // rendering on every page). SSR + first client render use DEFAULT_CURRENCY,
  // then we adopt the cookie value on mount. Safe because price display reads
  // from a currency-independent precomputed map, so SSR HTML is cacheable.
  useEffect(() => {
    const match = document.cookie.match(/(?:^|;\s*)currency=([^;]+)/);
    const stored = match ? decodeURIComponent(match[1]) : null;
    if (stored && stored !== DEFAULT_CURRENCY && currencies.some((c) => c.code === stored)) {
      setSelectedCurrency(stored);
    }
  }, []);

  const updateCurrency = (currency: string) => {
    if (!currencies.some((c) => c.code === currency)) {
      return;
    }

    setSelectedCurrency(currency);
    document.cookie = `${COOKIE_NAME}=${currency};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
  };

  return (
    <CurrencyContext.Provider value={{ selectedCurrency, updateCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrencyContext = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrencyContext must be used within a CurrencyProvider');
  }
  return context;
};
